/**
 * Product Optimized Subscription Handler
 * Handles subscription/one-time selection, selling plans, and UI state
 * for the product-optimized section
 *
 * This is isolated from shared JS to avoid hardcoded logic from other templates
 */
class ProductOptimizedSubscription extends HTMLElement {
  constructor() {
    super();
    this.rtxContainer = null;
    this.subscriptionOption = null;
    this.oneTimeOption = null;
    this.subscriptionRadio = null;
    this.oneTimeRadio = null;
    this.sellingPlanSelector = null;
    this.addToCartButton = null;
    this.isSubscriptionInput = null;
    this.productId = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Find RTX container
    this.rtxContainer = this.querySelector('.rtx-buy-box-parent');
    if (!this.rtxContainer) {
      console.warn('ProductOptimizedSubscription: No .rtx-buy-box-parent found');
      return;
    }

    // Mark as optimized to prevent other scripts from initializing
    this.rtxContainer.setAttribute('data-optimized-subscription', 'true');
    this.setAttribute('data-optimized-initialized', 'true');

    // Get product ID from container
    this.productId = this.rtxContainer.id?.replace('rtx_buy_box_', '');

    // Find subscription and one-time option elements
    this.subscriptionOption = this.rtxContainer.querySelector('.rtx_option_sub');
    this.oneTimeOption = this.rtxContainer.querySelector('.rtx_option_one_time');

    // Find radio inputs
    this.subscriptionRadio = this.rtxContainer.querySelector('input[value="subsave"]');
    this.oneTimeRadio = this.rtxContainer.querySelector('input[value="onetime"]');

    // Find selling plan selector
    this.sellingPlanSelector = this.rtxContainer.querySelector('.selling-plan-selector, select[name="selling_plan"]');

    // Find isSubscription hidden input
    this.isSubscriptionInput = this.rtxContainer.querySelector('input[name="isSubscription"]');

    // Find add to cart button (may be outside this element)
    this.addToCartButton = this.findAddToCartButton();

    // Setup event listeners
    this.setupEventListeners();

    // Set initial state
    this.updateUIState();

    // Listen for variant changes
    this.setupVariantChangeListener();
  }

  /**
   * Find the add to cart button (may be outside this element)
   */
  findAddToCartButton() {
    // Try to find within the form first
    const form = this.closest('form');
    if (form) {
      const button = form.querySelector('.main-cta-text');
      if (button) return button;
    }

    // Try to find in the page
    return document.querySelector('.main-cta-text');
  }

  /**
   * Set up event listeners for radio changes
   */
  setupEventListeners() {
    const radios = this.rtxContainer.querySelectorAll('input[name="selling_plan_select"]');

    radios.forEach(radio => {
      // Mark radio to prevent duplicate handlers
      radio.setAttribute('data-optimized-radio', 'true');

      // Use capture phase and stop propagation to prevent jQuery handlers
      radio.addEventListener('change', (e) => {
        e.stopImmediatePropagation(); // Prevent jQuery handler from firing
        this.handleSelectionChange();
      }, true);
    });

    // Listen for selling plan selector changes
    if (this.sellingPlanSelector) {
      this.sellingPlanSelector.addEventListener('change', () => {
        this.handleSellingPlanChange();
      });
    }
  }

  /**
   * Handle subscription/one-time selection change
   */
  handleSelectionChange() {
    const isSubscription = this.subscriptionRadio?.checked;
    const isOneTime = this.oneTimeRadio?.checked;

    // Update selling plan selector state
    if (this.sellingPlanSelector) {
      if (isSubscription) {
        this.sellingPlanSelector.disabled = false;
        this.sellingPlanSelector.name = 'selling_plan';
      } else {
        this.sellingPlanSelector.disabled = true;
        this.sellingPlanSelector.removeAttribute('name');
      }
    }

    // Update isSubscription input
    if (this.isSubscriptionInput) {
      this.isSubscriptionInput.value = isSubscription ? 'true' : 'false';
    }

    // Update UI state (colors, classes, button text)
    this.updateUIState();

    // Dispatch custom event
    this.dispatchSubscriptionChangeEvent(isSubscription);
  }

  /**
   * Handle selling plan selector change
   */
  handleSellingPlanChange() {
    if (!this.sellingPlanSelector) return;

    const selectedValue = this.sellingPlanSelector.value;
    const selectedOption = this.sellingPlanSelector.options[this.sellingPlanSelector.selectedIndex];
    const selectedText = selectedOption?.textContent || '';

    // Dispatch event with selling plan details
    const event = new CustomEvent('selling-plan:change', {
      detail: {
        sellingPlanId: selectedValue,
        sellingPlanName: selectedText,
        productId: this.productId
      },
      bubbles: true,
      composed: true
    });

    this.dispatchEvent(event);
  }

  /**
   * Update UI state based on current selection
   */
  updateUIState() {
    const isSubscription = this.subscriptionRadio?.checked;
    const isOneTime = this.oneTimeRadio?.checked;

    // Update subscription option styling
    if (this.subscriptionOption) {
      if (isSubscription) {
        this.subscriptionOption.classList.add('selected');
        this.subscriptionOption.classList.remove('unselected');
      } else {
        this.subscriptionOption.classList.add('unselected');
        this.subscriptionOption.classList.remove('selected');
      }
    }

    // Update one-time option styling
    if (this.oneTimeOption) {
      if (isOneTime) {
        this.oneTimeOption.classList.add('selected');
        this.oneTimeOption.classList.remove('unselected');
      } else {
        this.oneTimeOption.classList.add('unselected');
        this.oneTimeOption.classList.remove('selected');
      }
    }

    // Update add-to-cart button text
    this.updateButtonText(isSubscription, isOneTime);
  }

  /**
   * Update add to cart button text
   */
  updateButtonText(isSubscription, isOneTime) {
    if (!this.addToCartButton) return;

    if (isSubscription) {
      this.addToCartButton.textContent = 'Subscribe';
    } else if (isOneTime) {
      this.addToCartButton.textContent = 'Add to Bag';
    }
  }

  /**
   * Listen for variant changes to update selling plans
   */
  setupVariantChangeListener() {
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.productId === this.productId) {
        this.handleVariantChange(event.detail.variant);
      }
    });
  }

  /**
   * Handle variant change - update available selling plans
   */
  handleVariantChange(variant) {
    if (!this.sellingPlanSelector) return;

    // Get full variant data from product JSON (includes selling_plan_allocations)
    const fullVariant = this.getFullVariantData(variant.id);
    if (!fullVariant) {
      this.disableSubscriptionOption();
      return;
    }

    // Get selling plan allocations for this variant
    const sellingPlanAllocations = fullVariant.selling_plan_allocations || [];

    // In Shopify JSON, allocations only have selling_plan_id and selling_plan_group_id
    // We need to match these to the product's selling_plan_groups to get full details
    const rechargeAllocations = sellingPlanAllocations.map(allocation => {
      // Get product data to match selling plan group
      const productJsonEl = document.querySelector('[id^="product-json-"]');
      if (productJsonEl) {
        try {
          const productData = JSON.parse(productJsonEl.textContent);
          const sellingPlanGroups = productData.selling_plan_groups || [];

          // Find the matching group
          const matchingGroup = sellingPlanGroups.find(group => group.id === allocation.selling_plan_group_id);

          if (matchingGroup) {
            // Find the specific selling plan within the group
            const sellingPlan = matchingGroup.selling_plans?.find(plan => plan.id === allocation.selling_plan_id);

            if (sellingPlan) {
              // Attach the full selling plan to the allocation
              return { ...allocation, selling_plan: sellingPlan };
            }
          }
        } catch (e) {
          console.error('Failed to parse product JSON', e);
        }
      }

      // Return allocation as-is if we can't find the selling plan details
      return allocation;
    });

    // Update selector options
    this.updateSellingPlanOptions(rechargeAllocations);

    // If no selling plans available, disable subscription option
    if (rechargeAllocations.length === 0) {
      this.disableSubscriptionOption();
    } else {
      this.enableSubscriptionOption();
    }
  }

  /**
   * Get full variant data from product JSON
   */
  getFullVariantData(variantId) {
    // Try to get from product JSON script tag
    const productJsonEl = document.querySelector('[id^="product-json-"]');
    if (productJsonEl) {
      try {
        const productData = JSON.parse(productJsonEl.textContent);
        return productData.variants?.find(v => v.id === variantId);
      } catch (e) {
        console.error('ProductOptimizedSubscription: Failed to parse product JSON', e);
      }
    }
    return null;
  }

  /**
   * Update selling plan selector options
   */
  updateSellingPlanOptions(allocations) {
    if (!this.sellingPlanSelector) return;

    // Store current selection
    const currentValue = this.sellingPlanSelector.value;

    // Clear existing options
    this.sellingPlanSelector.innerHTML = '';

    if (allocations.length === 0) {
      // No options available
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'No options available';
      this.sellingPlanSelector.appendChild(option);
      this.sellingPlanSelector.disabled = true;
      return;
    }

    // Add new options
    allocations.forEach(allocation => {
      const sellingPlan = allocation.selling_plan;
      const option = document.createElement('option');
      option.value = sellingPlan.id;

      // Format the selling plan name
      let planName = sellingPlan.name || '';
      if (planName.includes('1 months')) {
        planName = 'Every 1 month';
      } else {
        planName = planName.replace('*', '');
      }

      option.textContent = planName;
      this.sellingPlanSelector.appendChild(option);
    });

    // Try to restore previous selection, or select first option
    if (currentValue && this.sellingPlanSelector.querySelector(`option[value="${currentValue}"]`)) {
      this.sellingPlanSelector.value = currentValue;
    } else {
      this.sellingPlanSelector.selectedIndex = 0;
    }

    // Enable selector if subscription is selected
    if (this.subscriptionRadio?.checked) {
      this.sellingPlanSelector.disabled = false;
    }

    // Update data attribute for downstream code
    this.sellingPlanSelector.dataset.rechargeSellingPlansCount = allocations.length.toString();
  }

  /**
   * Disable subscription option when variant has no selling plans
   */
  disableSubscriptionOption() {
    if (!this.subscriptionRadio || !this.oneTimeRadio) return;

    // Disable subscription radio
    this.subscriptionRadio.disabled = true;

    // If subscription was selected, switch to one-time
    if (this.subscriptionRadio.checked) {
      this.oneTimeRadio.checked = true;
      this.handleSelectionChange();
    }

    // Add disabled styling to subscription option
    if (this.subscriptionOption) {
      this.subscriptionOption.classList.add('disabled');
      this.subscriptionOption.style.opacity = '0.5';
      this.subscriptionOption.style.pointerEvents = 'none';
    }
  }

  /**
   * Enable subscription option when variant has selling plans
   */
  enableSubscriptionOption() {
    if (!this.subscriptionRadio) return;

    // Enable subscription radio
    this.subscriptionRadio.disabled = false;

    // Remove disabled styling from subscription option
    if (this.subscriptionOption) {
      this.subscriptionOption.classList.remove('disabled');
      this.subscriptionOption.style.opacity = '';
      this.subscriptionOption.style.pointerEvents = '';
    }
  }

  /**
   * Dispatch custom event when subscription state changes
   */
  dispatchSubscriptionChangeEvent(isSubscription) {
    const event = new CustomEvent('subscription:change', {
      detail: {
        isSubscription: isSubscription,
        sellingPlanId: isSubscription && this.sellingPlanSelector ? this.sellingPlanSelector.value : null,
        productId: this.productId
      },
      bubbles: true,
      composed: true
    });

    this.dispatchEvent(event);
  }
}

// Register the custom element
if (!customElements.get('product-optimized-subscription')) {
  customElements.define('product-optimized-subscription', ProductOptimizedSubscription);
}
